import { MenuCategory, MenuData, MenuItem } from './nodes.types';

/**
 * these are types for the menudata from the backend.
 * DO NOT USE THESE ANYWHERE ELSE.
 */
interface RawModelItem {
  id: string;
  displayName: string;
  description: string | null;
  icon: string | null;
  searchText: string;
  inputTypes: string[];
  outputTypes: string[];
  isLeaf: boolean;
  order: number | null;
  price: number | null;
  isNew: boolean | null;
  modelName: string;
  isVerified: boolean | null;
  termsUrls?: string[] | null;
  privacyPolicyUrl?: string | null;
  isByok?: boolean | null | undefined;
}

// Interface for items that can have children (non-leaf nodes)
interface RawMenuCategory {
  id: string;
  children: (RawMenuCategory | RawModelItem)[];
  order?: number;
}

interface RawSavedItem {
  id: string;
  displayName: string;
  description: string | null;
  icon: string | null;
  searchText: string;
  inputTypes: string[];
  outputTypes: string[];
  isLeaf: boolean;
  sourceNodeDefinitionId?: string;
}

// Interface for the main menu structure
interface RawMenuData {
  menu: {
    models: RawMenuCategory[];
    tools: RawMenuCategory[];
    communityModels: RawMenuCategory[];
    savedModels?: RawSavedItem[];
    savedNodes?: RawSavedItem[];
  };
  recent: RawModelItem[];
}

const isRawMenuCategory = (item: RawMenuCategory | RawModelItem): item is RawMenuCategory => {
  return 'children' in item;
};

const sortByOrder = <T extends { order?: number | null }>(items: T[]): T[] => {
  return items.slice().sort((a, b) => {
    const orderA = a.order ?? Infinity;
    const orderB = b.order ?? Infinity;
    return orderA - orderB;
  });
};

const sortCategoriesRecursively = (category: RawMenuCategory): MenuCategory => {
  if (!category || !Array.isArray(category.children))
    return {
      id: category.id,
      order: category.order,
      children: [],
    };

  const children = sortByOrder(category.children).map((child) => {
    if (isRawMenuCategory(child)) {
      return sortCategoriesRecursively(child);
    }

    return {
      id: child.id,
      displayName: child.displayName,
      description: child.description,
      icon: child.icon,
      searchText: child.searchText,
      inputTypes: child.inputTypes,
      outputTypes: child.outputTypes,
      isLeaf: child.isLeaf,
      price: child.price,
      isNew: child.isNew,
      order: child.order,
      name: child.modelName,
      isVerified: Boolean(child.isVerified),
      termsUrls: child.termsUrls,
      privacyPolicyUrl: child.privacyPolicyUrl,
      isByok: child.isByok,
    } as MenuItem;
  });

  return {
    id: category.id,
    children,
  };
};

// The API returns saved items as the same MenuItemChildDto as regular items, but with some fields
// unpopulated (price, order, etc.). Fill in defaults so they can go through sortCategoriesRecursively.
const toRawModelItem = (item: RawSavedItem): RawModelItem => ({
  ...item,
  order: null,
  price: null,
  isNew: null,
  modelName: item.displayName,
  isVerified: null,
});

interface ParseMenuOptions {
  includeSavedTemplates?: boolean;
  /**
   * When true, drops the server-nested `id: 'userModels'` subsection from
   * `customModels`. Set on the FF=on path so legacy user-saves don't surface
   * in the old Custom Models section, search, or add-node menu — community
   * models remain visible. See WEA-4036.
   */
  excludeUserModels?: boolean;
}

export const parseMenuData = (menuData: RawMenuData, options?: ParseMenuOptions): MenuData => {
  const { tools, models, communityModels, savedModels, savedNodes } = menuData.menu;
  const includeSaved = options?.includeSavedTemplates && (savedModels || savedNodes);
  const filteredCommunityModels = options?.excludeUserModels
    ? communityModels.filter((entry) => entry.id !== 'userModels')
    : communityModels;

  return {
    recent: sortCategoriesRecursively({ id: 'recent', children: menuData.recent }),
    ...(includeSaved
      ? {
          savedModels: sortCategoriesRecursively({
            id: 'saved_models',
            children: (savedModels ?? []).map(toRawModelItem),
          }),
          savedNodes: sortCategoriesRecursively({
            id: 'saved_nodes',
            children: (savedNodes ?? []).map(toRawModelItem),
          }),
        }
      : {}),
    tools: sortCategoriesRecursively({ id: 'tools', children: tools }),
    imageModels: sortCategoriesRecursively({
      ...(models.find((model) => model.id === 'image_models') || ({} as RawMenuCategory)),
      id: 'image_models',
    }),
    videoModels: sortCategoriesRecursively({
      ...(models.find((model) => model.id === 'video_models') || ({} as RawMenuCategory)),
      id: 'video_models',
    }),
    threedModels: sortCategoriesRecursively({
      ...(models.find((model) => model.id === 'threed_models') || ({} as RawMenuCategory)),
      id: 'threed_models',
    }),
    customModels: sortCategoriesRecursively({ id: 'custom_models', children: filteredCommunityModels }),
  };
};
